import { jsx as _jsx } from "react/jsx-runtime";
import clsx from "clsx";
import i18n from "../../../../../infrastructure/i18n/i18n";
import Tag from "../../../commons/tags";
import { container } from "tsyringe";
/**
 * Displays a list of recommended location tags for quick selection in the search bar.
 *
 * @component
 * @param props - Component props.
 * @param props.setSelectedPickupLocation - Callback to set the selected pickup location.
 * @param props.setSelectedReturnLocation - Callback to set the selected return location.
 *
 * @remarks
 * - Shows up to 3 recommended locations, excluding when the URL contains "collections".
 * - Each tag displays a translated or aliased name, and clicking a tag sets the pickup location.
 * - Location names are processed for capitalization and formatting.
 * - Uses i18n for language detection and translation selection.
 */
export const RecommendedLocations = (props) => {
    // Resolver DI
    const dataStore = container.resolve("IUseStoreAplication");
    // -- End Resolver DI
    const { setSelectedPickupLocation, setSelectedReturnLocation } = props;
    const { recommendedLocations } = dataStore.app;
    const url = window.location.href;
    const currentLanguage = i18n.language;
    const capitalizeWords = (str) => {
        return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
    };
    return (_jsx("div", { className: clsx("searchbar__tags"), children: recommendedLocations &&
            !url.includes("collections") &&
            url.includes("pages") &&
            recommendedLocations.slice(0, 3).map((tag, index) => {
                var _a;
                if (tag === null || tag === void 0 ? void 0 : tag.error) {
                    console.error("Error in recommended location tag data:", tag);
                    return null; // Skip rendering this tag if there's an error|
                }
                // Normalize the i18n language code to the base two-letter form (e.g., "en-US" → "EN").
                // Using exact equality instead of `.includes()` avoids partial matches (e.g., "E"
                // matching "EN") and the false-positive where an empty `translation.language` would
                // match any current language via `.includes("")`.
                const lang = (currentLanguage !== null && currentLanguage !== void 0 ? currentLanguage : "").split("-")[0].toUpperCase();
                const translatedTag = ((_a = tag.translations) === null || _a === void 0 ? void 0 : _a.find((translation) => { var _a; return ((_a = translation.language) !== null && _a !== void 0 ? _a : "").toUpperCase() === lang; })) || { name: tag.name, shortLabel: tag.shortLabel };
                const tagName = (() => {
                    // Priority 1: use translated shortLabel when the current language has a matching translations[] entry
                    if (translatedTag.shortLabel != null && translatedTag.shortLabel !== "") {
                        return translatedTag.shortLabel;
                    }
                    // Priority 2: use root-level shortLabel (typical for EN — the base language not listed in translations[])
                    if (tag.shortLabel != null && tag.shortLabel !== "") {
                        return tag.shortLabel;
                    }
                    // Fallback: derive from `name` after the first comma (trimmed), or use `name` as-is if no comma
                    const text = tag.name || "";
                    if (!text.includes(",")) {
                        return text;
                    }
                    return text
                        .split(",")
                        .slice(1)
                        .join(",")
                        .trim();
                })();
                const proccesedName = (translatedTag.name.includes(",")
                    ? translatedTag.name.split(",")[1]
                    : translatedTag.name != null && translatedTag.name != ""
                        ? translatedTag.name
                        : tag.name)
                    .split(" ") // Split into individual words
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
                    .join(" ") +
                    (tag.alias != null && tag.alias !== "" ? ", " + tag.alias : "") +
                    ", " +
                    capitalizeWords(tag.city) +
                    ", " +
                    capitalizeWords(tag.state);
                return (_jsx("div", { onClick: () => {
                        setSelectedPickupLocation([
                            tag.location_type == "2" ? proccesedName : tag.name,
                            tag.code || "",
                            tag.location_type || "",
                        ]);
                        setSelectedReturnLocation(["", "", ""]);
                    }, style: { cursor: "pointer" }, children: _jsx(Tag, { title: tagName, location_type: tag.location_type, cssClass: "outline blur", icon: tag.location_type }, index) }, tagName + index));
            }) }));
};
